// ═══════════════════════════════════════════════════════════════
// XYQAR — Admin Users — list users, assign roles, view MFA status
// Role: admin / super_admin
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, apiPost } from '../lib/api';
import {
  PageTitle, Section, Kpi, Btn, Table, Th, Td, fmt, EmptyState,
} from '../components/ui-kit';
import { UserPlus, RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';

interface User {
  user_id:        string;
  email:          string;
  email_verified: boolean;
  roles:          string[];
  mfa_enrolled:   boolean;
  last_login?:    string;
  created_at?:    string;
  blocked?:       boolean;
}

const ASSIGNABLE_ROLES = ['client', 'expert', 'partner', 'exec', 'auditor', 'admin'];

export default function Users() {
  const { getToken } = useXyqarAuth();

  const [users,      setUsers]     = React.useState<User[] | null>(null);
  const [q,          setQ]         = React.useState('');
  const [busy,       setBusy]      = React.useState<string | null>(null);
  const [msg,        setMsg]       = React.useState<string | null>(null);
  const [invEmail,   setInvEmail]  = React.useState('');
  const [invRole,    setInvRole]   = React.useState('client');
  const [showInvite, setShowInvite] = React.useState(false);

  const tok = React.useCallback(() => getToken().catch(() => ''), [getToken]);

  const load = React.useCallback(async () => {
    try {
      const t = await tok();
      const j = await apiGet<{ users: User[] }>('/api/v1/admin/users', t);
      setUsers(j.users ?? []);
    } catch { setUsers([]); }
  }, [tok]);

  React.useEffect(() => { load(); }, [load]);

  const flash = (text: string) => {
    setMsg(text);
    setTimeout(() => setMsg(null), 4000);
  };

  const assignRole = async (userId: string, role: string) => {
    setBusy(userId + role);
    try {
      const t = await tok();
      await apiPost('/api/v1/admin/users/assign-role', { user_id: userId, role }, t);
      await load();
      flash(`Role '${role}' assigned.`);
    } catch (e: any) {
      flash(e.message ?? 'Failed to assign role');
    } finally { setBusy(null); }
  };

  const invite = async () => {
    if (!invEmail.trim()) return;
    setBusy('invite');
    try {
      const t = await tok();
      await apiPost('/api/v1/admin/users/invite', { email: invEmail.trim(), role: invRole }, t);
      setInvEmail(''); setShowInvite(false);
      await load();
      flash(`Invitation sent to ${invEmail.trim()} with role '${invRole}'.`);
    } catch (e: any) {
      flash(e.message ?? 'Failed to invite');
    } finally { setBusy(null); }
  };

  const filtered = (users ?? []).filter((u) =>
    !q || u.email.toLowerCase().includes(q.toLowerCase()) ||
    u.roles.join(' ').includes(q.toLowerCase())
  );

  const mfaEnrolled = (users ?? []).filter((u) => u.mfa_enrolled).length;
  const verified    = (users ?? []).filter((u) => u.email_verified).length;
  const admins      = (users ?? []).filter((u) => u.roles.includes('admin') || u.roles.includes('super_admin')).length;

  const inp = 'rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40';

  return (
    <div>
      <PageTitle
        kicker="IAM"
        title="Users & roles"
        right={
          <div className="flex gap-2">
            <Btn onClick={() => load()} icon={<RefreshCw className="h-4 w-4" />}>Refresh</Btn>
            <Btn primary onClick={() => setShowInvite((v) => !v)} icon={<UserPlus className="h-4 w-4" />}>Invite</Btn>
          </div>
        }
      />

      {msg && (
        <div className="mb-4 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white/80 font-mono">
          {msg}
        </div>
      )}

      {showInvite && (
        <Section title="Invite new user" hint="Sends an identity invitation email with the selected role pre-assigned">
          <div className="flex flex-wrap gap-3 items-end mb-2">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Email *</label>
              <input
                className={inp}
                placeholder="user@company.com"
                value={invEmail}
                onChange={(e) => setInvEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && invite()}
              />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Role</label>
              <select
                className={inp + ' cursor-pointer'}
                value={invRole}
                onChange={(e) => setInvRole(e.target.value)}
              >
                {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Btn primary onClick={invite} disabled={busy === 'invite' || !invEmail.trim()}>
              {busy === 'invite' ? 'Sending…' : 'Send invitation'}
            </Btn>
          </div>
        </Section>
      )}

      <Section title="At a glance">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Total users"    value={fmt(users?.length)} />
          <Kpi accent="green" label="Email verified" value={fmt(verified)} hint={`of ${fmt(users?.length)}`} />
          <Kpi accent={mfaEnrolled === (users?.length ?? 0) ? 'green' : 'amber'} label="MFA enrolled" value={fmt(mfaEnrolled)} hint="TOTP enforced" />
          <Kpi               label="Admins"         value={fmt(admins)} />
        </div>
      </Section>

      <Section
        title="User directory"
        hint="Click a role badge to assign. Xyqar Identity syncs the change on next login."
        right={
          <input
            className={inp + ' w-60'}
            placeholder="Search email or role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search users"
          />
        }
      >
        {users && filtered.length === 0 && (
          <EmptyState title={q ? 'No users match your search' : 'No users yet'} hint="Use Invite above to add the first user." />
        )}
        {filtered.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Roles</Th>
                <Th>Email</Th>
                <Th>MFA</Th>
                <Th>Last login</Th>
                <Th>Assign role</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id}>
                  <Td className="font-mono text-xs">{u.email}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {(u.roles ?? []).map((r) => (
                        <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-white/70">{r}</span>
                      ))}
                      {(u.roles ?? []).length === 0 && <span className="text-white/30 text-xs">none</span>}
                    </div>
                  </Td>
                  <Td>
                    {u.email_verified
                      ? <ShieldCheck className="h-4 w-4 text-emerald-400" />
                      : <ShieldOff className="h-4 w-4 text-amber-400" />}
                  </Td>
                  <Td>
                    {u.mfa_enrolled
                      ? <ShieldCheck className="h-4 w-4 text-emerald-400" />
                      : <ShieldOff className="h-4 w-4 text-white/30" />}
                  </Td>
                  <Td className="text-white/40 text-xs">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : '—'}
                  </Td>
                  <Td>
                    <select
                      className="rounded bg-white/5 border border-white/10 px-2 py-1 text-xs text-white/60 focus:outline-none focus:border-white/30 cursor-pointer"
                      defaultValue=""
                      onChange={(e) => { if (e.target.value) assignRole(u.user_id, e.target.value); e.target.value = ''; }}
                      aria-label={`Assign role to ${u.email}`}
                    >
                      <option value="">+ assign…</option>
                      {ASSIGNABLE_ROLES.filter((r) => !(u.roles ?? []).includes(r)).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
